import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  Calendar,
  PenLine,
  Search,
  BookOpen,
  Calculator,
  TrendingUp,
  BarChart3,
  Target,
  Lightbulb,
  Flame,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import type { BlogPost } from "@shared/schema";
import {
  BLOG_CATEGORIES,
  getCategoryForPost,
  getPostsByCategory,
  searchPosts,
} from "@/lib/blog-categories";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "startup-finance": <BarChart3 size={20} />,
  "growth-metrics": <TrendingUp size={20} />,
  "market-research": <Target size={20} />,
  "founder-strategy": <Lightbulb size={20} />,
};

const FEATURED_GUIDES = [
  {
    title: "Startup Burn Rate Explained",
    slug: "what-is-startup-burn-rate",
    category: "Startup Finance",
  },
  {
    title: "LTV / CAC Complete Guide",
    slug: "what-is-ltv-cac-ratio",
    category: "Growth & SaaS Metrics",
  },
  {
    title: "Market Size for Startups",
    slug: "what-are-tam-sam-som",
    category: "Market Research",
  },
];

const POPULAR_CALCULATORS = [
  {
    title: "Burn Rate Calculator",
    href: "/calculators/burn-rate",
    description: "Track monthly cash consumption",
  },
  {
    title: "Valuation Calculator",
    href: "/calculators/startup-valuation",
    description: "Estimate your startup valuation",
  },
  {
    title: "TAM / SAM / SOM Calculator",
    href: "/calculators/tam-sam-som",
    description: "Size your addressable market",
  },
];

const POPULAR_ARTICLE_SLUGS = [
  "how-to-calculate-burn-rate-step-by-step",
  "good-ltv-cac-ratio-saas",
  "burn-rate-benchmarks-by-stage",
  "tam-vs-sam-vs-som-differences",
  "how-investors-evaluate-burn-rate-due-diligence",
  "why-ltv-cac-most-important-saas-metric",
  "how-much-runway-should-startups-maintain",
  "common-market-sizing-mistakes-pitch-decks",
];

function ArticleCard({ post }: { post: BlogPost }) {
  const catSlug = getCategoryForPost(post.slug);
  const cat = BLOG_CATEGORIES.find((c) => c.slug === catSlug);
  return (
    <Link href={`/blog/${post.slug}`}>
      <article
        data-testid={`card-post-${post.id}`}
        className="bg-card border border-white/5 rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 cursor-pointer group"
      >
        <div className="flex items-center gap-3 mb-4">
          {cat && (
            <Badge variant="outline" className="text-xs border-primary/30 text-primary">
              {cat.name}
            </Badge>
          )}
          <Calendar size={14} className="text-muted-foreground" aria-hidden="true" />
          <time className="text-sm text-muted-foreground">
            Last Updated:{" "}
            {new Date(post.updatedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </time>
        </div>
        <h2 className="text-2xl font-display font-bold mb-3 group-hover:text-primary transition-colors">
          {post.title}
        </h2>
        <p className="text-muted-foreground leading-relaxed mb-6">{post.excerpt}</p>
        <span className="text-primary font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
          Read Article <ArrowRight size={16} />
        </span>
      </article>
    </Link>
  );
}

export default function Blog() {
  const { user } = useAuth();
  const isOwner = ["matt@mattrebeiro.com", "matt@learnpdi.com"].includes(user?.email || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/posts", "published"],
    queryFn: async () => {
      const res = await fetch("/api/posts?published=true");
      if (!res.ok) throw new Error("Failed to fetch posts");
      return res.json();
    },
  });

  /** Hub "Latest" list: show every published post; sort by last update so newly edited/imported posts surface first. */
  const postsSortedForFeed = useMemo(() => {
    if (!posts?.length) return [];
    return [...posts].sort((a, b) => {
      const byUpdated = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      if (byUpdated !== 0) return byUpdated;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [posts]);

  const isSearching = searchQuery.trim().length > 0;
  const isFiltering = isSearching || !!activeCategory;

  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    let result = posts;
    if (isSearching) {
      result = searchPosts(result, searchQuery);
    }
    if (activeCategory) {
      result = getPostsByCategory(result, activeCategory);
    }
    return result;
  }, [posts, searchQuery, activeCategory, isSearching]);

  const categoryCounts = useMemo(
    () =>
      posts
        ? BLOG_CATEGORIES.reduce(
            (acc, cat) => {
              acc[cat.slug] = getPostsByCategory(posts, cat.slug).length;
              return acc;
            },
            {} as Record<string, number>
          )
        : {},
    [posts]
  );

  const popularArticles = useMemo(() => {
    if (!posts) return [];
    return POPULAR_ARTICLE_SLUGS.map((slug) => posts.find((p) => p.slug === slug)).filter(
      (p): p is BlogPost => !!p
    );
  }, [posts]);

  const showHubs = !isFiltering;

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Blog</span>
              </div>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-4">Stories & Lessons</h1>
                  <p className="text-xl text-muted-foreground max-w-2xl">
                    Insights to help you achieve the right mindset, strategy, and execution plan to succeed in startups and enterprises.
                  </p>
                </div>
                {isOwner && (
                  <Link href="/admin">
                    <Button className="shrink-0" data-testid="button-write-article">
                      <PenLine size={16} className="mr-2" /> Write Article
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* 1. Search Bar */}
            <div className="mb-8">
              <div className="relative max-w-2xl">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  data-testid="input-search-blog"
                  type="text"
                  aria-label="Search articles by title or topic"
                  placeholder="Search articles by title or topic..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 py-6 bg-card border-white/10 text-base rounded-xl focus:border-primary/50"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
                    data-testid="button-clear-search"
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-3 mb-12" role="group" aria-label="Filter articles by category">
              <button
                onClick={() => setActiveCategory(null)}
                data-testid="filter-all"
                aria-current={!activeCategory ? "page" : undefined}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
                  !activeCategory
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"
                }`}
              >
                All Articles {posts ? `(${posts.length})` : ""}
              </button>
              {BLOG_CATEGORIES.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(activeCategory === cat.slug ? null : cat.slug)}
                  data-testid={`filter-${cat.slug}`}
                  aria-current={activeCategory === cat.slug ? "page" : undefined}
                  aria-pressed={activeCategory === cat.slug}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 min-h-[44px] ${
                    activeCategory === cat.slug
                      ? "bg-primary text-primary-foreground"
                      : "bg-card border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"
                  }`}
                >
                  {CATEGORY_ICONS[cat.slug]}
                  {cat.name} {categoryCounts[cat.slug] ? `(${categoryCounts[cat.slug]})` : ""}
                </button>
              ))}
            </div>

            {/* Filtered / Search Results View */}
            {isFiltering && (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-display font-semibold">
                      {isSearching
                        ? `Search results for "${searchQuery}"`
                        : BLOG_CATEGORIES.find((c) => c.slug === activeCategory)?.name}
                    </h2>
                    <span className="text-sm text-muted-foreground" role="status" aria-live="polite">
                      {filteredPosts.length} article{filteredPosts.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                {isLoading ? (
                  <div className="space-y-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-card border border-white/5 rounded-2xl p-8 animate-pulse">
                        <div className="h-4 bg-white/5 rounded w-1/4 mb-4" />
                        <div className="h-8 bg-white/5 rounded w-3/4 mb-4" />
                        <div className="h-4 bg-white/5 rounded w-full" />
                      </div>
                    ))}
                  </div>
                ) : filteredPosts.length > 0 ? (
                  <div className="space-y-6">
                    {filteredPosts.map((post) => (
                      <ArticleCard key={post.id} post={post} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 bg-card border border-white/5 rounded-2xl">
                    <Search size={32} className="mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-xl font-display font-semibold mb-3">No articles found</h3>
                    <p className="text-muted-foreground mb-6">
                      {isSearching
                        ? `No results matching "${searchQuery}". Try a different search term.`
                        : "No articles in this category yet."}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveCategory(null);
                      }}
                      data-testid="button-reset-filters"
                      aria-label="Clear all search filters"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Default Hub View */}
            {showHubs && !isLoading && (
              <>
                {/* 2. Featured Guides */}
                <div className="mb-12">
                  <h2 className="text-lg font-display font-semibold mb-4 flex items-center gap-2">
                    <BookOpen size={18} className="text-primary" />
                    Featured Guides
                  </h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    {FEATURED_GUIDES.map((guide) => (
                      <Link key={guide.slug} href={`/blog/${guide.slug}`}>
                        <div
                          data-testid={`featured-guide-${guide.slug}`}
                          className="bg-card border border-white/10 rounded-xl p-6 hover:border-primary/40 transition-all group cursor-pointer"
                        >
                          <Badge variant="outline" className="mb-3 text-xs border-primary/30 text-primary">
                            {guide.category}
                          </Badge>
                          <h3 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                            {guide.title}
                          </h3>
                          <span className="text-primary text-sm mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read Guide <ArrowRight size={14} />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* 3. Calculator Tools */}
                <div className="mb-12">
                  <h2 className="text-lg font-display font-semibold mb-4 flex items-center gap-2">
                    <Calculator size={18} className="text-primary" />
                    Calculator Tools
                  </h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    {POPULAR_CALCULATORS.map((calc) => (
                      <Link key={calc.href} href={calc.href}>
                        <div
                          data-testid={`featured-calc-${calc.href.split("/").pop()}`}
                          className="bg-card border border-white/10 rounded-xl p-6 hover:border-primary/40 transition-all group cursor-pointer"
                        >
                          <Calculator size={16} className="text-primary mb-3" />
                          <h3 className="font-display font-semibold group-hover:text-primary transition-colors">
                            {calc.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">{calc.description}</p>
                          <span className="text-primary text-sm mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
                            Open Calculator <ArrowRight size={14} />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* 4. Topic Categories */}
                <div className="mb-12">
                  <h2 className="text-2xl font-display font-bold mb-6">Browse by Topic</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {BLOG_CATEGORIES.map((cat) => {
                      const catPosts = posts ? getPostsByCategory(posts, cat.slug) : [];
                      return (
                        <Link key={cat.slug} href={`/blog/${cat.slug}`}>
                          <div
                            data-testid={`hub-${cat.slug}`}
                            className="bg-card border border-white/10 rounded-2xl p-8 hover:border-primary/30 transition-all group cursor-pointer h-full"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="p-3 bg-primary/10 rounded-xl text-primary">
                                {CATEGORY_ICONS[cat.slug]}
                              </div>
                              <Badge variant="outline" className="text-xs border-white/20 text-muted-foreground">
                                {catPosts.length} articles
                              </Badge>
                            </div>
                            <h3 className="text-xl font-display font-bold mb-2 group-hover:text-primary transition-colors">
                              {cat.name}
                            </h3>
                            <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{cat.description}</p>
                            <div className="space-y-1.5 mb-4">
                              {catPosts.slice(0, 3).map((post) => (
                                <p key={post.id} className="text-sm text-muted-foreground truncate">
                                  &bull; {post.title}
                                </p>
                              ))}
                              {catPosts.length > 3 && (
                                <p className="text-sm text-muted-foreground">
                                  &bull; +{catPosts.length - 3} more articles
                                </p>
                              )}
                            </div>
                            <span className="text-primary font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                              View All <ArrowRight size={14} />
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* 5. Latest Articles */}
                {postsSortedForFeed.length > 0 && (
                  <div className="mb-12">
                    <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-3">
                      Latest Articles
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2">
                      {postsSortedForFeed.map((post) => (
                        <ArticleCard key={post.id} post={post} />
                      ))}
                    </div>
                  </div>
                )}

                {/* 6. Popular Articles */}
                {popularArticles.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-3">
                      <Flame size={22} className="text-primary" />
                      Popular Articles
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      {popularArticles.map((post) => {
                        const catSlug = getCategoryForPost(post.slug);
                        const cat = BLOG_CATEGORIES.find((c) => c.slug === catSlug);
                        return (
                          <Link key={post.id} href={`/blog/${post.slug}`}>
                            <div
                              data-testid={`popular-post-${post.id}`}
                              className="bg-card border border-white/5 rounded-xl p-6 hover:border-primary/30 transition-all group cursor-pointer h-full"
                            >
                              {cat && (
                                <Badge variant="outline" className="mb-3 text-xs border-primary/30 text-primary">
                                  {cat.name}
                                </Badge>
                              )}
                              <h3 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                                {post.title}
                              </h3>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>
                              <span className="text-primary text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                                Read Article <ArrowRight size={14} />
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
